import React, { FC, useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../../components/Header";
import styled from "@emotion/styled";
import generator from "generate-password";
import ManagesOperators from './ManagesOperators';

import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import {
  BackButton,
  BackText,
  CommonButton,
  Container,
  Content,
  CustomTab,
  FullNameEmail,
  InputColumn,
  TextButton,
  Title,
  TitleText,
} from "../../constants/styles";
import { labels } from "../../constants/labels";
import {
  Backdrop,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog as MuiDialog,
  TextField,
} from "@mui/material";
import { RootState, useAppDispatch } from "../../store";
import {
  Operator,
  changeOperatorApprovalStatus,
  editOperatorStatus,
  editOperatorSalesCoachEnabled,
  editOperatorMenuCoachEnabled,
  editOperatorMenuCoachPortalUrl,
  fetchOperator,
  fetchOperators,
  getServers,
  resendInvitation,
  resendServerInvitation,
  updateOperator,
  updateServer,
} from "../../features/operator/operatorSlice";
import ShortInfo from "../../components/ShortInfo";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { default as StatusOperator } from "./Status";
import Status from "../../components/Status";
import { OperatorRepresentative, PasswordGenerateRow } from "../addOperator";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import OutlineButton from "../../components/OutlineButton";
import { useAlert } from "react-alert";
import { useSelector } from "react-redux";
import { OperatorStatus, Role } from "../../constants/types";
import Dialog from "../../components/Dialog";
import MoreButton from "../../components/MoreButton";
import InputInfo from "../../components/InputInfo";
import FillButton from "../../components/FillButton";
import SuccessAlert from "../../components/SuccessAlert";
import { useIsSmallUI } from "../../constants/utils";
import ControlRow from "./controlRow";
import RejectedReason from "./rejectedReason";
import Reason from "../management/reason";

interface Props {}

export enum FilterStatus {
  Total = "total",
  Completed = "completed",
  NotStarted = "notStarted",
  InProgress = "inProgress",
  NotJoined = "notJoined",
  PendingApproval = "Pending",
  Rejected = "Rejected",
}

const operatorLabels = {
  managesOperators: 'Branch Operators',
  addOperator: 'Add Operator',
  selectOperator: 'Select Operator',
  edit: 'Edit',
  delete: 'Delete',
  // Add other custom labels as needed
};

// Default module chapter counts - fallback values
const DEFAULT_CHAPTER_COUNTS: { [key: string]: number } = {
  wine: 24,
  spirits: 8,
  beer: 24,
  beer1: 24,
  barossa: 24,
  bob: 24,
  rosé: 24,
  rose: 24,
  winterwarmers: 24,
  default: 24
};

// Helper to get chapter count for a module - uses fetched chapter counts
const getModuleChapterCount = (moduleId: string | undefined, chapterCounts: { [key: string]: number }): number => {
  if (!moduleId) return chapterCounts.default || DEFAULT_CHAPTER_COUNTS.default;
  
  const normalizedId = moduleId.toLowerCase().replace(/\d/g, '');
  const lowerModuleId = moduleId.toLowerCase();
  
  // Try fetched counts first (both normalized and original)
  if (chapterCounts[normalizedId]) {
    return chapterCounts[normalizedId];
  }
  if (chapterCounts[lowerModuleId]) {
    return chapterCounts[lowerModuleId];
  }
  
  // Fallback to defaults
  return DEFAULT_CHAPTER_COUNTS[normalizedId] || DEFAULT_CHAPTER_COUNTS.default;
};

// Check if a server has started any module - reads directly from database fields
const hasStartedAnyModule = (server: any): boolean => {
  // Check legacy system fields from database
  const currentChapterIndex = server.currentChapterIndex ?? server["currentChapterIndex"] ?? 0;
  const currentPageIndex = server.currentPageIndex ?? server["currentPageIndex"] ?? 0;
  
  if (currentChapterIndex > 0 || currentPageIndex > 0) {
    return true;
  }
  
  // Check moduleProgressDetails array from database
  const moduleProgressDetails = server.moduleProgressDetails || server["moduleProgressDetails"];
  if (moduleProgressDetails && Array.isArray(moduleProgressDetails) && moduleProgressDetails.length > 0) {
    return moduleProgressDetails.some((module: any) => {
      const chapterIndex = module.currentChapterIndex ?? module.chapterIndex ?? 0;
      const pageIndex = module.currentPageIndex ?? module.pageIndex ?? 0;
      return chapterIndex > 0 || pageIndex > 0;
    });
  }
  return false;
};

// Check if ALL modules with progress are completed - reads directly from database fields
const hasCompletedAllModules = (server: any, chapterCounts: { [key: string]: number }): boolean => {
  // Get moduleProgressDetails directly from database
  const moduleProgressDetails = server.moduleProgressDetails || server["moduleProgressDetails"];
  
  // Must have moduleProgressDetails to be considered complete
  if (!moduleProgressDetails || !Array.isArray(moduleProgressDetails) || moduleProgressDetails.length === 0) {
    return false;
  }
  
  // Get modules that have any progress (started modules only)
  const startedModules = moduleProgressDetails.filter((module: any) => {
    const chapterIndex = module.currentChapterIndex ?? module.chapterIndex ?? 0;
    const pageIndex = module.currentPageIndex ?? module.pageIndex ?? 0;
    return chapterIndex > 0 || pageIndex > 0;
  });
  
  // If no modules started, not complete
  if (startedModules.length === 0) {
    return false;
  }
  
  // Check if all started modules are completed
  return startedModules.every((module: any) => {
    const moduleId = module.id || module.moduleId || '';
    const chapterIndex = module.currentChapterIndex ?? module.chapterIndex ?? 0;
    // Use fetched chapter counts
    const totalChapters = getModuleChapterCount(moduleId, chapterCounts);
    return chapterIndex >= totalChapters;
  });
};

const OperatorDetail: FC<Props> = () => {
  const navigate = useNavigate();
  const [operator, setOperator] = useState<Operator>();
  const [serverListLoaded, setServerListLoaded] = useState<boolean>(false);
  const [serverList, setServerList] = useState([]);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [currentFilter, setCurrentFilter] = useState<string>(
    FilterStatus.Total
  );
  const dispatch = useAppDispatch();
  const [operatorName, setOperatorName] = useState<string>("");
  const { operatorId } = useParams();
  const [fullName, setFullName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [password, setPassword] = useState<string>("");
  const [showServerPassword, setShowServerPassword] = useState<boolean>(false);
  const [serverPassword, setServerPassword] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const alert = useAlert();
  const user = useSelector((state: RootState) => state.user.user);
  const [confirmationPopup, setConfirmationPopup] = useState<boolean>(false);
  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
  const [serverFirstName, setServerFirstName] = useState<string>("");
  const [serverLastName, setServerLastName] = useState<string>("");
  const [serverEmail, setServerEmail] = useState<string>("");
  const [currentChoosingServer, setCurrentChoosingServer] = useState<any>();
  const [isSmallUI, setIsSmallUI] = useIsSmallUI(window.innerWidth < 768);
  const [shouldDisplaySuccessAlert, setShouldDisplaySuccessAlert] =
    useState<boolean>(false);
  const [isRejectPopupOpen, setIsRejectPopupOpen] = useState<boolean>(false);
  const [rejectReason, setRejectReason] = useState<string>("");
  
  // Module chapter counts fetched from API
  const [moduleChapterCounts, setModuleChapterCounts] = useState<{ [key: string]: number }>(DEFAULT_CHAPTER_COUNTS);

  // Fetch module chapter counts from API on mount
  useEffect(() => {
    const fetchChapterCounts = async () => {
      try {
        // Counts are already available from this app's backend. Keeping this
        // same-origin avoids sending the production Uno build to knowbie-back-dev
        // and avoids a browser-to-backend call that cannot use apiAuth's token.
        const response = await fetch("/api/servers/get-module-chapter-counts", {
          credentials: "same-origin",
        });
        if (!response.ok) {
          throw new Error(`Chapter counts request failed: ${response.status}`);
        }
        const data = await response.json();
        const counts = data?.chapterCounts;
        if (counts && typeof counts === "object") {
          // Normalize all keys to lowercase
          const normalizedCounts = Object.keys(counts).reduce((acc, key) => {
            acc[key.toLowerCase()] = counts[key];
            return acc;
          }, {} as { [key: string]: number });
          setModuleChapterCounts(normalizedCounts);
        }
      } catch (error) {
        console.log('Could not fetch module chapter counts, using defaults');
      }
    };
    fetchChapterCounts();
  }, []);

  useEffect(() => {
    if (!operatorId || operator) return;
    dispatch(
      fetchOperator({
        operatorId: operatorId as string,
        successCallback: (data) => {
          setOperator(data);
          setOperatorName(data.organization);
          setFullName(data.name);
          setEmail(data.email || "");
          setPassword(data.password || "");
        },
        errorCallback: (error) => console.log("error", error),
      })
    );
  }, [dispatch, operator, operatorId]);

  useEffect(() => {
    !serverListLoaded &&
      dispatch(
        getServers({
          operatorId: operatorId as string,
          successCallback: (data) => {
            setServerList(data);
            setServerListLoaded(true);
          },
        })
      );
  }, [dispatch, operator, operatorId, serverList, serverListLoaded]);

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setCurrentFilter(newValue as FilterStatus);
  };

  const isChanged = useMemo(() => {
    if (!operator) return false;
    return (
      operator.organization !== operatorName ||
      operator.name !== fullName ||
      operator.email !== email ||
      operator.password !== password
    );
  }, [email, fullName, operator, operatorName, password]);

  const isEmpty = useMemo(() => {
    return !operatorName || !fullName || !email || !password;
  }, [email, fullName, operatorName, password]);

  const shouldDisable = useMemo(() => {
    return isEmpty || !isChanged;
  }, [isEmpty, isChanged]);

  const onStatusChange = () => {
    operator &&
      dispatch(
        editOperatorStatus({
          operatorId: operatorId || "",
          status: !operator.isActive,
          onSuccess: () =>
            dispatch(
              fetchOperator({
                operatorId: operatorId as string,
                successCallback: (data) => setOperator(data),
              })
            ),
        })
      );
  };

  const onMenuCoachToggle = () => {
    operator &&
      dispatch(
        editOperatorMenuCoachEnabled({
          operatorId: operatorId || "",
          enabled: !operator.menuCoachEnabled,
          onSuccess: () =>
            dispatch(
              fetchOperator({
                operatorId: operatorId as string,
                successCallback: (data) => setOperator(data),
              })
            ),
        })
      );
  };

  // Resolves with null on success (after the operator is refetched) or the
  // server's message so the control row can show it inline.
  const onMenuCoachPortalUrlSave = (url: string): Promise<string | null> =>
    new Promise((resolve) => {
      if (!operator) return resolve("Operator not loaded yet.");
      dispatch(
        editOperatorMenuCoachPortalUrl({
          operatorId: operatorId || "",
          url,
          onSuccess: () => {
            dispatch(
              fetchOperator({
                operatorId: operatorId as string,
                successCallback: (data) => setOperator(data),
              })
            );
            resolve(null);
          },
          onError: (message) => resolve(message),
        })
      );
    });

  const onSalesCoachToggle = () => {
    operator &&
      dispatch(
        editOperatorSalesCoachEnabled({
          operatorId: operatorId || "",
          enabled: !operator.salesCoachEnabled,
          onSuccess: () =>
            dispatch(
              fetchOperator({
                operatorId: operatorId as string,
                successCallback: (data) => setOperator(data),
              })
            ),
        })
      );
  };

  const onBackToOperatorList = () => {
    dispatch(fetchOperators({}));
    navigate("/");
  };

  const onGeneratePassword = () => {
    const initialPassword = generator.generate({
      length: 6,
      numbers: false,
      uppercase: true,
      lowercase: false,
    });
    setPassword(initialPassword);
  };

  const onEditButtonPressed = () => {
    setIsEditMode(!isEditMode);
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleClickShowServerPassword = () =>
    setShowServerPassword((show) => !show);

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const onUpdateAndResendButtonPressed = () => {
    onUpdateButtonPressed(true);
  };

    const onUpdateButtonPressed = (shouldResendInvitation: boolean = false) => {
        if (operator) {
            setIsLoading(true);
            dispatch(
                updateOperator({
                    operatorData: {
                        name: fullName,
                        email: email,
                        password: password,
                        organization: operatorName,
                    },
                    isUpdatingEmail: operator.email !== email,
                    isUpdatingPassword: operator.password !== password,
                    operatorId: operatorId || "",
                    successCallback: () =>
                        dispatch(
                            fetchOperator({
                                operatorId: operatorId as string,
                                successCallback: (data) => {
                                    setIsLoading(false);
                                    setOperator(data);
                                    setOperatorName(data.organization);
                                    setFullName(data.name);
                                    setEmail(data.email || "");
                                    setPassword(data.password || "");
                                    alert.show("Operator information has been updated", {
                                        type: "success",
                                    });
                                    if (shouldResendInvitation) {
                                        alert.show("Resending invite", {
                                            type: "info",
                                        });
                                        dispatch(
                                            resendInvitation({
                                                operatorId: operatorId || "",
                                                successCallback: () =>
                                                    alert.show("An invite has been resent successfully", {
                                                        type: "success",
                                                    }),
                                                errorCallback: (error) =>
                                                    alert.show("Resending Invitation failed", {
                                                        type: "error",
                                                    }),
                                            })
                                        );
                                    }
                                },
                                errorCallback: (error) => {
                                    setIsLoading(false);
                                    alert.show(error || "Failed to fetch operator details", {
                                        type: "error",
                                    });
                                },
                            })
                        ),
                })
            )
                .unwrap()
                .catch((error) => {
                    setIsLoading(false);
                    alert.show(error || "Failed to update operator", {
                        type: "error",
                    });
                });
        }
    };
  const courseCompleted = useMemo(() => {
    return serverList.filter((server) => hasCompletedAllModules(server, moduleChapterCounts));
  }, [serverList, moduleChapterCounts]);

  const inProgress = useMemo(() => {
    return serverList.filter(
      (server) =>
        server["status"] !== "Invited" &&
        hasStartedAnyModule(server) &&
        !hasCompletedAllModules(server, moduleChapterCounts)
    );
  }, [serverList, moduleChapterCounts]);

  const notStarted = useMemo(() => {
    return serverList.filter(
      (server) =>
        server["status"] !== "Invited" &&
        !hasStartedAnyModule(server)
    );
  }, [serverList]);

  const notJoined = useMemo(() => {
    return serverList.filter((server) => server["status"] === "Invited");
  }, [serverList]);

  const getStatusType = (row: any) => {
    if (row["status"] === "Invited") {
      return FilterStatus.NotJoined;
    } else if (hasCompletedAllModules(row, moduleChapterCounts)) {
      return FilterStatus.Completed;
    } else if (hasStartedAnyModule(row)) {
      return FilterStatus.InProgress;
    } else {
      return FilterStatus.NotStarted;
    }
  };

  // Render progress details for each module - reads directly from moduleProgressDetails in database
  const renderProgressDetails = (server: any) => {
    if (server["status"] === "Invited") {
      return <ProgressText style={{ color: '#9CA3AF' }}>Not started</ProgressText>;
    }

    // Get moduleProgressDetails directly from the database/server object
    const moduleProgressDetails = server.moduleProgressDetails || server["moduleProgressDetails"];
    const currentChapterIndex = server.currentChapterIndex || server["currentChapterIndex"] || 0;

    // Use moduleProgressDetails from database to show all modules with progress
    if (moduleProgressDetails && Array.isArray(moduleProgressDetails) && moduleProgressDetails.length > 0) {
      const progressItems: JSX.Element[] = [];

      for (const moduleProgress of moduleProgressDetails) {
        // Get chapter index directly from database - support both naming conventions
        const chapterIndex = moduleProgress.currentChapterIndex ?? moduleProgress.chapterIndex ?? 0;
        const pageIndex = moduleProgress.currentPageIndex ?? moduleProgress.pageIndex ?? 0;
        const moduleId = moduleProgress.id || moduleProgress.moduleId || '';

        // Skip modules with no progress
        if (chapterIndex === 0 && pageIndex === 0) {
          continue;
        }

        // Skip modules with blank/empty id
        if (!moduleId || moduleId.trim() === '') {
          continue;
        }

        // Use the id from database directly as the module name (letters only)
        const lettersOnly = moduleId.replace(/\d/g, ''); // Remove all numbers
        const displayName = lettersOnly.charAt(0).toUpperCase() + lettersOnly.slice(1);

        // Get total chapters for this module using fetched chapter counts
        const totalChapters = getModuleChapterCount(moduleId, moduleChapterCounts);

        if (chapterIndex >= totalChapters) {
          // Module completed
          progressItems.push(
            <ProgressRow key={`progress-${moduleId}`}>
              <ModuleName>{displayName}</ModuleName>
              <DoneBadge>Done</DoneBadge>
            </ProgressRow>
          );
        } else {
          // Module in progress - show the chapter number directly from database
          progressItems.push(
            <ProgressRow key={`progress-${moduleId}`}>
              <ModuleName>{displayName}</ModuleName>
              <ChapterBadge>Chp {chapterIndex}</ChapterBadge>
            </ProgressRow>
          );
        }
      }

      return (
        <ProgressContainer>
          {progressItems.length > 0 ? progressItems : (
            <ProgressText style={{ color: '#9CA3AF' }}>No progress</ProgressText>
          )}
        </ProgressContainer>
      );
    } else if (currentChapterIndex > 0) {
      // Legacy single-module system fallback (use currentChapterIndex from database)
      const totalChapters = getModuleChapterCount('wine', moduleChapterCounts);

      return (
        <ProgressRow>
          <ModuleName>Wine</ModuleName>
          {currentChapterIndex >= totalChapters ? (
            <DoneBadge>Done</DoneBadge>
          ) : (
            <ChapterBadge>Chp {currentChapterIndex}</ChapterBadge>
          )}
        </ProgressRow>
      );
    } else {
      // No progress at all
      return <ProgressText style={{ color: '#9CA3AF' }}>No progress</ProgressText>;
    }
  };

  const onCancelEdit = () => {
    setIsEditMode(false);
    setFullName(operator?.name || "");
    setEmail(operator?.email || "");
    setPassword(operator?.password || "");
  };

  const onApproveButtonPressed = () => setConfirmationPopup(true);

  const onConfirmApprove = () => {
    setConfirmationPopup(false);
    dispatch(
      changeOperatorApprovalStatus({
        email: operator?.email || "",
        status: OperatorStatus.Approved,
        successCallback: () =>
          dispatch(
            fetchOperator({
              operatorId: operatorId as string,
              successCallback: (data) => {
                setOperator(data);
                alert.show("Operator has been approved", {
                  type: "success",
                });
              },
              errorCallback: (error) => console.log("error", error),
            })
          ),
      })
    );
  };

  const onRejectOperator = () => {
    setIsLoading(true);
    setIsRejectPopupOpen(false);
    dispatch(
      changeOperatorApprovalStatus({
        email: operator?.email || "",
        rejectReason: rejectReason,
        status: OperatorStatus.Rejected,
        successCallback: () => {
          dispatch(
            fetchOperator({
              operatorId: operatorId as string,
              successCallback: (data) => {
                setOperator(data);
                alert.show("Operator has been rejected", {
                  type: "success",
                });
                setRejectReason("");
                setIsLoading(false);
              },
              errorCallback: (error) => console.log("error", error),
            })
          );
        },
      })
    );
  };

  const onRejectButtonPressed = () => setIsRejectPopupOpen(true);

  const onCloseButtonPressed = () => setEditDialogOpen(false);

  const onResendInviteServerSuccess = () => {
    setShouldDisplaySuccessAlert(true);
    setTimeout(() => {
      setShouldDisplaySuccessAlert(false);
    }, 7000);
  };

  const onResendInviteServer = () => {
    dispatch(
      resendServerInvitation({
        serverId: currentChoosingServer?._id || "",
        successCallback: () => onResendInviteServerSuccess(),
        errorCallback: () =>
          alert.show("Resending Invitation failed", {
            type: "error",
          }),
      })
    );
  };

  const onCancelConfirmation = () => {
    setConfirmationPopup(false);
    setIsRejectPopupOpen(false);
  };

  const onUpdateServer = () => {
    dispatch(
      updateServer({
        serverId: currentChoosingServer?._id || "",
        serverData: {
          firstName: serverFirstName,
          lastName: serverLastName,
          email: serverEmail,
        },
        successCallback: () => {
          alert.show(labels.serverInfoUpdated, {
            type: "success",
          });
          setEditDialogOpen(false);
          dispatch(
            getServers({
              operatorId: operatorId as string,
              successCallback: (data) => {
                setServerList(data);
              },
            })
          );
        },
      })
    );
  };

  const serverOptions = (server: any) => {
    return [
      {
        label: labels.edit,
        onClick: () => {
          setEditDialogOpen(true);
          setCurrentChoosingServer(server);
          server && setServerFirstName(server["first_name"] || "");
          server && setServerLastName(server["last_name"] || "");
          server && setServerEmail(server["email"] || "");
          server && setServerPassword(server["initialPassword"] || "");
        },
      },
      {
        label: labels.resendInvite,
        onClick: () =>
          dispatch(
            resendServerInvitation({
              serverId: server?._id || "",
              successCallback: () =>
                alert.success(
                  `${labels.successResendInviteServer} ${server["first_name"]} ${server["last_name"]}`
                ),
              errorCallback: () =>
                alert.show("Resending Invitation failed", {
                  type: "error",
                }),
            })
          ),
      },
    ];
  };

  const CustomTabPanel = ({ value, list }: { value: string; list: any }) => {
    return (
      <TabPanel value={value} sx={{ padding: 0 }}>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 750 }} size="small" aria-label="a dense table">
            <TableHead>
              <CustomTableRow>
                <TableCell sx={TableCellCustomStyle}>
                  {labels.fullName}
                </TableCell>
                <TableCell sx={TableCellCustomStyle}>{labels.email}</TableCell>
                <TableCell sx={TableCellCustomStyle}>{labels.status}</TableCell>
                <TableCell sx={TableCellCustomStyle}>Progress</TableCell>
                <TableCell sx={TableCellCustomStyle}>
                  {labels.inviteDate}
                </TableCell>
                <TableCell sx={TableCellCustomStyle}>
                  {labels.userRegistered}
                </TableCell>
                <TableCell sx={TableCellCustomStyle}></TableCell>
              </CustomTableRow>
            </TableHead>
            <TableBody>
              {list.map(
                (
                  row: { [x: string]: any },
                  index: React.Key | null | undefined
                ) => (
                  <TableRow
                    key={index}
                    sx={{
                      backgroundColor: "#fff",
                    }}
                  >
                    <TableCell
                      sx={cellStyle}
                    >{`${row["first_name"]} ${row["last_name"]}`}</TableCell>
                    <TableCell sx={cellStyle}>{row["email"]}</TableCell>
                    <TableCell>
                      <StatusOperator
                        type={getStatusType(row) || FilterStatus.Completed}
                      />
                    </TableCell>
                    <TableCell sx={{ ...cellStyle, minWidth: 140 }}>
                      {renderProgressDetails(row)}
                    </TableCell>
                    <TableCell sx={cellStyle}>
                      {new Date(row["invitedDate"]).toLocaleString("default", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </TableCell>
                    <TableCell sx={cellStyle}>
                      {row["userRegistered"]
                        ? new Date(row["userRegistered"]).toLocaleString(
                            "default",
                            {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            }
                          )
                        : "-"}
                    </TableCell>
                    <TableCell sx={cellStyle}>
                      <MoreButton options={serverOptions(row)} />
                    </TableCell>
                  </TableRow>
                )
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>
    );
  };

  return (
    <Container>
      <MuiDialog
        open={confirmationPopup || isRejectPopupOpen}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <Dialog
          title={labels.confirmation}
          content={
            confirmationPopup
              ? labels.approveAdminConfirmation
              : labels.rejectAdminConfirmation
          }
          declineText={labels.cancelDeleteAdmin}
          onDecline={onCancelConfirmation}
          acceptText={confirmationPopup ? labels.yesApprove : labels.yesReject}
          onAccept={confirmationPopup ? onConfirmApprove : onRejectOperator}
        >
          {isRejectPopupOpen && (
            <Reason
              rejectReason={rejectReason}
              setRejectReason={setRejectReason}
            />
          )}
        </Dialog>
      </MuiDialog>
      <MuiDialog open={editDialogOpen}>
        <EditServerContainer>
          <DialogTitle>{labels.editServer}</DialogTitle>
          {shouldDisplaySuccessAlert && (
            <SuccessAlert
              message={`${labels.successResendInviteServer} ${currentChoosingServer["first_name"]} ${currentChoosingServer["last_name"]}`}
            />
          )}
          <CustomInputInfo>
            <InputInfo
              title={labels.firstName}
              value={serverFirstName}
              onChange={(e) => setServerFirstName(e.target.value)}
              placeholder={labels.enterFirstName}
            />
          </CustomInputInfo>
          <CustomInputInfo>
            <InputInfo
              title={labels.lastName}
              value={serverLastName}
              onChange={(e) => setServerLastName(e.target.value)}
              placeholder={labels.enterLastName}
            />
          </CustomInputInfo>
          <CustomInputInfo>
            <InputInfo
              disabled={true}
              title={labels.email}
              value={serverEmail}
              onChange={(e) => setServerEmail(e.target.value)}
              placeholder={labels.enterEmail}
            />
          </CustomInputInfo>
          <TitleText style={{ marginTop: 16 }}>{labels.password}</TitleText>
          <TextField
            disabled={true}
            type={showServerPassword ? "text" : "password"}
            sx={{ marginTop: 1 }}
            color="secondary"
            id="outlined-basic"
            variant="outlined"
            size="small"
            value={serverPassword}
            onChange={(e) => setServerPassword(e.target.value)}
            placeholder="Enter password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowServerPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showServerPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <ServerDialogControlRow>
            <OutlineButton onClick={onCloseButtonPressed}>
              {labels.close}
            </OutlineButton>
            <div style={{ marginLeft: 12 }}>
              <FillButton onClick={onResendInviteServer}>
                {labels.resendInvite}
              </FillButton>
            </div>
            <div style={{ marginLeft: 12 }}>
              <FillButton
                isDisabled={
                  !currentChoosingServer ||
                  (serverFirstName === currentChoosingServer["first_name"] &&
                    serverLastName === currentChoosingServer["last_name"] &&
                    serverEmail === currentChoosingServer["email"]) ||
                  !serverFirstName ||
                  !serverLastName ||
                  !serverEmail ||
                  !serverPassword
                }
                onClick={onUpdateServer}
              >
                {labels.update}
              </FillButton>
            </div>
          </ServerDialogControlRow>
        </EditServerContainer>
      </MuiDialog>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Header isSmallUI={typeof isSmallUI === "boolean" ? isSmallUI : false} />
      <Content
        style={
          isSmallUI ? { paddingTop: 14, paddingLeft: 16, paddingRight: 16 } : {}
        }
      >
        <div>
          <BackButton variant="text" onClick={onBackToOperatorList}>
            <ArrowBackIosIcon fontSize={"small"} color="secondary" />
            <BackText>{labels.backToOperatorList}</BackText>
          </BackButton>
        </div>
        <Title>{labels.operatorDetail}</Title>
        {!operator ? (
          <LinearProgress sx={{ marginTop: 3 }} color="secondary" />
        ) : (
          <>
            <Headline>{labels.operatorInformation}</Headline>
            {isEditMode ? (
              <InformationContainer>
                <TitleText>{labels.operatorName}</TitleText>
                <TextField
                  sx={{ marginTop: 1, maxWidth: 768 }}
                  color="secondary"
                  id="outlined-basic"
                  variant="outlined"
                  size="small"
                  value={operatorName}
                  onChange={(e) => setOperatorName(e.target.value)}
                  placeholder="Enter operator name"
                />
                <OperatorRepresentative>
                  {labels.operatorRepresentative}
                </OperatorRepresentative>
                <FullNameEmail>
                  <InputColumn>
                    <TitleText>{labels.fullName}</TitleText>
                    <TextField
                      sx={{ marginTop: 1 }}
                      color="secondary"
                      id="outlined-basic"
                      variant="outlined"
                      size="small"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="Enter full name"
                    />
                  </InputColumn>
                  <InputColumn>
                    <TitleText>{labels.email}</TitleText>
                    <TextField
                      sx={{ marginTop: 1 }}
                      color="secondary"
                      id="outlined-basic"
                      variant="outlined"
                      size="small"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter email"
                    />
                  </InputColumn>
                </FullNameEmail>
                <TitleText style={{ marginTop: 16 }}>Password</TitleText>
                <PasswordGenerateRow>
                  <TextField
                    type={showPassword ? "text" : "password"}
                    sx={{ width: "49%" }}
                    color="secondary"
                    id="outlined-basic"
                    variant="outlined"
                    size="small"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <div style={{ width: "49%" }}>
                    <Button
                      onClick={onGeneratePassword}
                      variant="outlined"
                      sx={generateButtonStyle}
                    >
                      {labels.generate}
                    </Button>
                  </div>
                </PasswordGenerateRow>
                <UpdateResendInviteRow>
                  <OutlineButton onClick={onCancelEdit}>
                    {labels.cancel}
                  </OutlineButton>
                  <CommonButton
                    sx={{ marginLeft: 1 }}
                    onClick={onUpdateAndResendButtonPressed}
                  >
                    {labels.updateAndResendInvite}
                  </CommonButton>
                  <CommonButton
                    disabled={shouldDisable}
                    sx={{ marginLeft: 1 }}
                    style={{
                      backgroundColor: shouldDisable ? "#DCCDE3" : "#2E1A46",
                      color: "white",
                    }}
                    onClick={() => onUpdateButtonPressed(false)}
                  >
                    {labels.update}
                  </CommonButton>
                </UpdateResendInviteRow>
              </InformationContainer>
            ) : (
              <InformationContainer>
                <TitleRow>
                  <TitleStatus
                    style={isSmallUI ? { flexDirection: "column" } : {}}
                  >
                    <OperatorTitle>{operator.organization}</OperatorTitle>
                    <div
                      style={
                        isSmallUI ? { marginTop: 8 } : statusContainerStyle
                      }
                    >
                      {operator.status === FilterStatus.PendingApproval ? (
                        <StatusOperator type={FilterStatus.PendingApproval} />
                      ) : operator.status === FilterStatus.Rejected ? (
                        <StatusOperator type={FilterStatus.Rejected} />
                      ) : (
                        <Status isEnable={operator.isActive}></Status>
                      )}
                    </div>
                  </TitleStatus>
                  <TextButton onClick={onEditButtonPressed}>Edit</TextButton>
                </TitleRow>
                <Divider />
                <SubtitleText>{labels.operatorRepresentative}</SubtitleText>
                <ShortInfoRow
                  style={
                    isSmallUI
                      ? {
                          flexDirection: "column",
                          alignItems: "flex-start",
                        }
                      : {}
                  }
                >
                  <ShortInfo title={labels.fullName} value={operator.name} />
                  <ShortInfo
                    style={isSmallUI ? { marginTop: 20 } : {}}
                    title={labels.email}
                    value={operator.email || ""}
                  />
                  {operator.status !== FilterStatus.PendingApproval ? (
                    <ShortInfo
                      style={isSmallUI ? { marginTop: 20 } : {}}
                      title={labels.password}
                      value={"**********"}
                    />
                  ) : (
                    <div style={{ width: 100 }}></div>
                  )}
                </ShortInfoRow>
                <ControlRow
                  operator={operator}
                  onApproveButtonPressed={onApproveButtonPressed}
                  onRejectButtonPressed={onRejectButtonPressed}
                  onStatusChange={onStatusChange}
                  onSalesCoachToggle={onSalesCoachToggle}
                  onMenuCoachToggle={onMenuCoachToggle}
              onMenuCoachPortalUrlSave={onMenuCoachPortalUrlSave}
                />
                {operator.status === FilterStatus.Rejected &&
                  operator.rejectReason !== undefined && (
                    <RejectedReason reason={operator.rejectReason || ""} />
                  )}
                <AddedByText
                  style={
                    isSmallUI
                      ? {
                          position: "relative",
                          marginTop: 48,
                          textAlign: "left",
                          bottom: 0,
                        }
                      : {}
                  }
                >
                  Added by: {operator.addedBy}
                </AddedByText>
              </InformationContainer>
            )}
            {
              <>
              <Headline>Management Information</Headline>
              <ManagesOperators
                labels={operatorLabels}
                resourceId={operator.id}
            />
              </>
            }
            {serverListLoaded && (
              <>
                <Headline>{labels.serversList}</Headline>
                <ServerContainer>
                  <TabContext value={currentFilter.toString()}>
                    <TabList
                      variant="scrollable"
                      onChange={handleChange}
                      aria-label="lab API tabs example"
                    >
                      <CustomTab
                        label={`${labels.allServers} (${serverList.length})`}
                        value={FilterStatus.Total.toString()}
                      />
                      <CustomTab
                        label={`${labels.courseCompleted} (${courseCompleted.length})`}
                        value={`${FilterStatus.Completed.toString()}`}
                      />
                      <CustomTab
                        label={`${labels.inProgress} (${inProgress.length})`}
                        value={FilterStatus.InProgress.toString()}
                      />
                      <CustomTab
                        label={`${labels.notStarted} (${notStarted.length})`}
                        value={FilterStatus.NotStarted.toString()}
                      />
                      <CustomTab
                        label={`${labels.notJoined} (${notJoined.length})`}
                        value={FilterStatus.NotJoined.toString()}
                      />
                    </TabList>
                    <CustomTabPanel
                      value={FilterStatus.Total.toString()}
                      list={serverList}
                    />
                    <CustomTabPanel
                      value={FilterStatus.Completed.toString()}
                      list={courseCompleted}
                    />
                    <CustomTabPanel
                      value={FilterStatus.InProgress.toString()}
                      list={inProgress}
                    />
                    <CustomTabPanel
                      value={FilterStatus.NotStarted.toString()}
                      list={notStarted}
                    />
                    <CustomTabPanel
                      value={FilterStatus.NotJoined.toString()}
                      list={notJoined}
                    />
                  </TabContext>
                </ServerContainer>
              </>
            )}
          </>
        )}
      </Content>
    </Container>
  );
};

const statusContainerStyle = {
  marginLeft: 16,
  alignItems: "center",
  justifyContent: "center",
  display: "flex",
};

const generateButtonStyle = {
  height: 40,
  width: 120,
  fontSize: 14,
  fontWeight: 700,
  fontFamily: "Mulish",
  color: "#6E4C8E",
};

const ServerDialogControlRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  margin-top: 32px;
`;

const EditServerContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 544px;
  background: #ffffff;
  box-shadow: 0px 20px 24px -4px rgba(16, 24, 40, 0.08),
    0px 8px 8px -4px rgba(16, 24, 40, 0.03);
  border-radius: 8px;
  padding: 24px;
`;

const AddedByText = styled.div`
  position: absolute;
  bottom: 32px;
  right: 32px;
  font-family: "Mulish";
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  text-align: right;
  color: #918f95;
`;

const UpdateResendInviteRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  margin-top: 48px;
`;

const cellStyle = {
  color: "#000000",
  fontFamily: "Mulish",
  fontWeight: 400,
  fontSize: 14,
  height: 44,
};

const TableCellCustomStyle = {
  color: "#81859A",
  fontFamily: "Mulish",
  fontWeight: 700,
  fontSize: 12,
  textTransform: "uppercase",
};

const CustomTableRow = styled(TableRow)`
  padding: 12px 24px;
  height: 44px;
  background-color: #f9fafb;
  box-shadow: none;
`;

const ServerContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 16px;
  width: 100%;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.08);
`;

const CustomInputInfo = styled.div`
  margin-top: 16px;
`;

const ShortInfoRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  max-width: 700px;
`;

const SubtitleText = styled.div`
  font-family: "Mulish";
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  color: #2e1a46;
  margin-top: 24px;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: #ebebeb;
  margin-top: 12px;
`;

const TitleStatus = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
`;

const TitleRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const OperatorTitle = styled.div`
  font-family: Mulish;
  font-style: normal;
  font-weight: 800;
  font-size: 24px;
  line-height: 32px;
  color: #2e1a46;
`;

const InformationContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 32px;
  padding-top: 12px;
  padding-left: 32px;
  padding-right: 32px;
  background-color: #ffffff;
  margin-top: 16px;
  border-radius: 8px;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.08);
  position: relative;
`;

const Headline = styled.div`
  font-family: "Mulish";
  font-style: normal;
  font-weight: 700;
  font-size: 20px;
  line-height: 30px;
  color: #2e1a46;
  margin-top: 48px;
`;

const DialogTitle = styled.div`
  font-family: "Mulish";
  font-style: normal;
  font-weight: 700;
  font-size: 18px;
  line-height: 28px;
  color: #2e1a46;
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;
`;

// Progress display styled components
const ProgressContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const ProgressRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const ModuleName = styled.span`
  font-family: "Mulish";
  font-size: 12px;
  font-weight: 500;
  color: #374151;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DoneBadge = styled.div`
  padding: 2px 8px;
  border-radius: 12px;
  font-family: "Mulish";
  font-size: 11px;
  font-weight: 600;
  background-color: #ECF4ED;
  color: #3B6E41;
  white-space: nowrap;
`;

const ChapterBadge = styled.div`
  padding: 2px 6px;
  border-radius: 4px;
  font-family: "Mulish";
  font-size: 11px;
  font-weight: 600;
  background-color: #FEF3C7;
  color: #B45309;
  white-space: nowrap;
`;

const ProgressText = styled.div`
  font-family: "Mulish";
  font-size: 12px;
`;

export default OperatorDetail;
